import React, { useState } from "react"
import { navigate } from "gatsby-link"
import isFinite from "lodash/isFinite"
import queryString from "query-string"
import {
    HorizontalStackSpaceBetween,
    Modal,
} from "@mallardbay/lib-react-components"

import { ListingType } from "~graphql/generated/graphql"
import {
    clearUrlQueryParams,
    getUrlQueryParamsObj,
} from "~utils/url-param-helpers"
import SearchFilters from "~components/pages/search/search-filters/search-filters"
import { parseInteger } from "~utils/parsing-helpers"
import COPY from "~config/copy-constants"

import SearchFiltersModalFooter from "./search-filters-modal-footer"

interface Props {
    readonly isOpen: boolean
    readonly onClose: () => void
}

function isValidListingType(value: string): value is ListingType {
    return Object.values<string>(ListingType).includes(value)
}

export default function SearchFiltersModal({ isOpen, onClose }: Props) {
    const {
        query = "",
        state,
        country,
        startDate: defaultStartDate,
        endDate: defaultEndDate,
        priceMin: defaultPriceMin,
        priceMax: defaultPriceMax,
        type: defaultType,
        species: defaultSpecies,
        amenities: defaultAmenities,
        hasIncludedLodging: defaultHasIncludedLodging,
    } = getUrlQueryParamsObj()

    const [type, setType] = useState<ListingType | undefined>(() => {
        if (
            typeof defaultType === "string" &&
            isValidListingType(defaultType)
        ) {
            return defaultType
        }
        return undefined
    })

    const [species, setSpecies] = useState<string[]>(
        typeof defaultSpecies === "string" && defaultSpecies.length > 0
            ? defaultSpecies.split(",")
            : []
    )
    const [amenities, setAmenities] = useState<string[]>(
        typeof defaultAmenities === "string" && defaultAmenities.length > 0
            ? defaultAmenities.split(",")
            : []
    )
    const [priceMin, setPriceMin] = useState<number | undefined>(
        defaultPriceMin && typeof defaultPriceMin === "string"
            ? parseInteger(defaultPriceMin)
            : undefined
    )
    const [priceMax, setPriceMax] = useState<number | undefined>(
        defaultPriceMax && typeof defaultPriceMax === "string"
            ? parseInteger(defaultPriceMax)
            : undefined
    )
    const [startDate, setStartDate] = useState<number | undefined>(
        defaultStartDate && typeof defaultStartDate === "string"
            ? parseInteger(defaultStartDate)
            : undefined
    )
    const [endDate, setEndDate] = useState<number | undefined>(
        defaultEndDate && typeof defaultEndDate === "string"
            ? parseInteger(defaultEndDate)
            : undefined
    )
    const [hasIncludedLodging, setHasIncludedLodging] = useState<
        true | undefined
    >(defaultHasIncludedLodging ? true : undefined)

    const [shouldClear, setShouldClear] = useState<boolean>(false)

    function handleApply() {
        const searchQueryString = queryString.stringify({
            query,
            type,
            state,
            country,
            startDate: isFinite(startDate) ? startDate : undefined,
            endDate: isFinite(endDate) ? endDate : undefined,
            priceMin: isFinite(priceMin) ? priceMin : undefined,
            priceMax: isFinite(priceMax) ? priceMax : undefined,
            species: species.length > 0 ? species.join(",") : undefined,
            amenities: amenities.length > 0 ? amenities.join(",") : undefined,
            hasIncludedLodging,
        })

        void navigate(`/search?${searchQueryString}`)
        onClose()
    }

    function handleClear() {
        setShouldClear(true)
        setPriceMin(undefined)
        setPriceMax(undefined)
        setType(undefined)
        setSpecies([])
        setAmenities([])
        setStartDate(undefined)
        setEndDate(undefined)
        setHasIncludedLodging(undefined)

        void clearUrlQueryParams()
        onClose()
    }

    return (
        <Modal
            title={COPY.FILTERS}
            isOpen={isOpen}
            onClose={onClose}
            shouldAddPadding
        >
            <SearchFilters
                priceMin={priceMin}
                setPriceMin={setPriceMin}
                priceMax={priceMax}
                setPriceMax={setPriceMax}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                type={type}
                setType={setType}
                species={species}
                setSpecies={setSpecies}
                amenities={amenities}
                setAmenities={setAmenities}
                hasIncludedLodging={hasIncludedLodging}
                setHasIncludedLodging={setHasIncludedLodging}
                shouldClear={shouldClear}
                setShouldClear={setShouldClear}
            />
            <HorizontalStackSpaceBetween>
                <SearchFiltersModalFooter
                    handleApply={handleApply}
                    handleClear={handleClear}
                />
            </HorizontalStackSpaceBetween>
        </Modal>
    )
}
